/* J5 storefront behaviour (Spark Oils × MANNOL). Loaded on every page by layout/theme.liquid.
 *  1. J5 select: replaces native <select> drop-downs with an accessible J5 combo box (button + listbox).
 *     The native select stays in the form (hidden) and receives the value + change/input events,
 *     so forms, Shopify filters and Dawn's sort still work. Opt out with data-native on the select.
 *  2. J5 product page: pack-size buttons, quantity stepper (capped at stock, and at the per-item maximum of 100,
 *     minus what is in the cart), Buy now, gallery thumbnails (a video thumb loads its YouTube player on press).
 *  3. Policy pages: last word of the title in the accent colour (as in the J5 design).
 *  4. Header search typeahead (Shopify predictive search /search/suggest.json), phone search toggle,
 *     cart pop-up position.
 *  5. In-cart stepper on product cards ("+" stops at the pack's stock).
 *  6. Oil finder filters on collection pages while Search & Discovery does not apply them.
 *  7. Announcement bar: shown only while its discount code is live (Storefront API cart check).
 *  8. Cart page GST details: GSTIN format check, saved as cart attributes as the shopper types.
 */
(function () {
  'use strict';

  // ---------- 1. J5 select ----------
  var uid = 0;
  var CHEVRON = '<svg class="j5-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6"/></svg>';
  var CHECK = '<svg class="j5-select__tick" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>';

  function enhance(select) {
    if (select.dataset.j5Select || select.multiple || select.size > 1 || select.hasAttribute('data-native')) return;
    if (select.closest('[data-native], .localization-form, .shopify-challenge__container')) return;
    select.dataset.j5Select = '1';
    var id = ++uid;
    var wrap = document.createElement('div');
    wrap.className = 'j5-select';
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    select.classList.add('j5-select__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'j5-select__button';
    btn.id = 'j5-select-btn-' + id;
    btn.setAttribute('role', 'combobox');
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'j5-select-list-' + id);
    btn.innerHTML = '<span class="j5-select__value"></span>' + CHEVRON;
    if (select.disabled) btn.disabled = true;

    var list = document.createElement('ul');
    list.className = 'j5-select__list';
    list.id = 'j5-select-list-' + id;
    list.setAttribute('role', 'listbox');
    list.tabIndex = -1;
    list.hidden = true;

    // Label: point an existing <label for> at the button; otherwise reuse aria-label.
    var label = select.id ? document.querySelector('label[for="' + CSS.escape(select.id) + '"]') : null;
    if (label) {
      if (!label.id) label.id = 'j5-select-lbl-' + id;
      label.htmlFor = btn.id;
      list.setAttribute('aria-labelledby', label.id);
    } else if (select.getAttribute('aria-label')) {
      btn.setAttribute('aria-label', select.getAttribute('aria-label'));
      list.setAttribute('aria-label', select.getAttribute('aria-label'));
    }

    wrap.appendChild(btn);
    wrap.appendChild(list);

    var active = -1;
    function options() { return Array.prototype.slice.call(select.options); }
    function build() {
      list.innerHTML = '';
      options().forEach(function (o, i) {
        var li = document.createElement('li');
        li.id = 'j5-select-opt-' + id + '-' + i;
        li.className = 'j5-select__option';
        li.setAttribute('role', 'option');
        li.dataset.index = i;
        li.innerHTML = '<span></span>' + CHECK;
        li.firstChild.textContent = o.textContent.trim();
        if (o.disabled) li.setAttribute('aria-disabled', 'true');
        list.appendChild(li);
      });
      sync();
    }
    function sync() {
      var o = select.options[select.selectedIndex];
      btn.querySelector('.j5-select__value').textContent = o ? o.textContent.trim() : '';
      btn.classList.toggle('is-placeholder', !!o && o.value === '');
      Array.prototype.forEach.call(list.children, function (li, i) { li.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false'); });
    }
    function setActive(i) {
      var items = list.children;
      if (!items.length) return;
      i = Math.max(0, Math.min(items.length - 1, i));
      if (active > -1 && items[active]) items[active].classList.remove('is-active');
      active = i;
      items[i].classList.add('is-active');
      btn.setAttribute('aria-activedescendant', items[i].id);
      var li = items[i];
      if (li.offsetTop < list.scrollTop) list.scrollTop = li.offsetTop;
      else if (li.offsetTop + li.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = li.offsetTop + li.offsetHeight - list.clientHeight;
    }
    function open() {
      if (btn.disabled || !list.hidden) return;
      closeAll(wrap);
      list.hidden = false;
      wrap.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      // Open upwards when there is no room below.
      var r = btn.getBoundingClientRect();
      wrap.classList.toggle('is-up', window.innerHeight - r.bottom < Math.min(280, list.scrollHeight + 16) && r.top > window.innerHeight - r.bottom);
      setActive(select.selectedIndex < 0 ? 0 : select.selectedIndex);
    }
    function close() {
      if (list.hidden) return;
      list.hidden = true;
      wrap.classList.remove('is-open', 'is-up');
      btn.setAttribute('aria-expanded', 'false');
      btn.removeAttribute('aria-activedescendant');
    }
    function choose(i) {
      var o = select.options[i];
      if (!o || o.disabled) return;
      var changed = select.selectedIndex !== i;
      select.selectedIndex = i;
      sync();
      close();
      btn.focus();
      if (changed) {
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    wrap._j5close = close;

    btn.addEventListener('click', function () { if (list.hidden) open(); else close(); });
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    list.addEventListener('click', function (e) {
      var li = e.target.closest('.j5-select__option');
      if (li) choose(+li.dataset.index);
    });
    list.addEventListener('mousemove', function (e) {
      var li = e.target.closest('.j5-select__option');
      if (li && +li.dataset.index !== active) setActive(+li.dataset.index);
    });
    var typed = '', typedAt = 0;
    btn.addEventListener('keydown', function (e) {
      var k = e.key;
      if (list.hidden) {
        if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ' || k === 'Alt') { e.preventDefault(); open(); }
        return;
      }
      if (k === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (k === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (k === 'Home') { e.preventDefault(); setActive(0); }
      else if (k === 'End') { e.preventDefault(); setActive(list.children.length - 1); }
      else if (k === 'PageDown') { e.preventDefault(); setActive(active + 6); }
      else if (k === 'PageUp') { e.preventDefault(); setActive(active - 6); }
      else if (k === 'Enter' || k === ' ') { e.preventDefault(); choose(active); }
      else if (k === 'Escape') { e.preventDefault(); close(); }
      else if (k === 'Tab') { close(); }
      else if (k.length === 1 && /\S/.test(k)) {
        var now = Date.now();
        typed = now - typedAt > 700 ? k.toLowerCase() : typed + k.toLowerCase();
        typedAt = now;
        var opts = options();
        for (var j = 0; j < opts.length; j++) { if (opts[j].textContent.trim().toLowerCase().indexOf(typed) === 0) { setActive(j); break; } }
      }
    });
    btn.addEventListener('blur', function () { setTimeout(function () { if (!wrap.contains(document.activeElement)) close(); }, 0); });
    select.addEventListener('change', sync);
    // Rebuild when the options change (Dawn re-renders filters and sort).
    new MutationObserver(build).observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'selected'] });
    build();
  }

  function closeAll(except) {
    document.querySelectorAll('.j5-select.is-open').forEach(function (w) { if (w !== except && w._j5close) w._j5close(); });
  }
  document.addEventListener('click', function (e) { if (!e.target.closest('.j5-select')) closeAll(); });

  function enhanceAll(root) {
    (root || document).querySelectorAll('select').forEach(enhance);
  }

  // ---------- 2. J5 product page ----------
  function money(paise) {
    var n = paise / 100;
    var s = n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
    return '₹' + s;
  }
  function unitPrice(v) {
    var m = String(v.title).toLowerCase().match(/^([\d.]+)\s*(l|ml|g|kg)\b/);
    if (!m) return '';
    var q = parseFloat(m[1]);
    if (!q) return '';
    var per = m[2] === 'l' ? v.price / q : m[2] === 'ml' ? v.price * 1000 / q : m[2] === 'g' ? v.price * 1000 / q : v.price / q;
    return money(Math.floor(per)) + (m[2] === 'g' || m[2] === 'kg' ? ' per kg' : ' per litre');
  }

  function initProduct(root) {
    if (root.dataset.j5Ready) return;
    root.dataset.j5Ready = '1';
    var data = root.querySelector('[data-j5-variants]');
    var variants = data ? JSON.parse(data.textContent) : [];
    var idInput = root.querySelector('[data-j5-id]');
    var qty = root.querySelector('.j5-qty__input');
    var add = root.querySelector('.j5-pdp__add');
    var now = root.querySelector('[data-j5-buynow]');
    var stage = root.querySelector('.j5-pdp__img');
    var stageBox = root.querySelector('.j5-pdp__stage');
    function q(sel) { return root.querySelectorAll(sel); }

    // A video thumb puts a YouTube player over the stage; any photo (thumb or pack change) takes it away again.
    function closeVideo() {
      var f = stageBox && stageBox.querySelector('.j5-pdp__video');
      if (!f) return;
      f.remove();
      q('.j5-pdp__thumb--video').forEach(function (x) { x.classList.remove('is-active'); x.setAttribute('aria-pressed', 'false'); });
    }
    function playVideo(t) {
      closeVideo();
      var f = document.createElement('iframe');
      f.className = 'j5-pdp__video';
      f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(t.dataset.video) + '?autoplay=1&rel=0&playsinline=1';
      f.title = t.dataset.alt || 'Product video';
      f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      f.allowFullscreen = true;
      // YouTube refuses embeds that arrive without a referrer (player "Error 153"); send the store's origin.
      f.referrerPolicy = 'strict-origin-when-cross-origin';
      stageBox.appendChild(f);
    }

    function show(v) {
      if (!v) return;
      if (idInput) idInput.value = v.id;
      q('[data-j5-price]').forEach(function (el) { el.textContent = money(v.price); });
      var hasMrp = v.compare > v.price;
      q('[data-j5-mrp]').forEach(function (el) { el.textContent = money(v.compare); el.hidden = !hasMrp; });
      q('[data-j5-mrp-decl]').forEach(function (el) { el.textContent = money(v.compare || v.price); });
      q('[data-j5-net]').forEach(function (el) { el.textContent = v.title; });
      q('[data-j5-unit]').forEach(function (el) { el.textContent = unitPrice(v); });
      q('[data-j5-stock]').forEach(function (el) { el.textContent = v.available ? 'In stock' : 'Sold out'; el.className = v.available ? 'is-in' : 'is-out'; });
      if (stage && v.image) closeVideo(), stage.src = v.image, stage.removeAttribute('srcset');
      q('.j5-pack').forEach(function (b) {
        var on = +b.dataset.variant === v.id;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      pendingNote = '';
      limit(v);
    }

    // Stock limit: the most the shopper can still add is the pack's stock minus what is already in the cart.
    // The quantity stepper stops there and the line under the buttons says why, so "Add to cart" never fails
    // with "sold out" when only the extra quantity is missing.
    var maxQty = parseInt(root.dataset.maxQty, 10) || 100; // per item, per order
    function current() { return variants.find(function (v) { return v.id === +idInput.value; }) || variants[0]; }
    function cap(v) { return Math.min(v.stock == null ? Infinity : v.stock, maxQty); }
    function left(v) { return Math.max(0, cap(v) - (v.inCart || 0)); }
    function note(html) { var el = root.querySelector('[data-j5-limit]'); if (!el) return; el.innerHTML = html || ''; el.hidden = !html; }
    function onlyLeft(v, l) { return 'Only ' + l + (v.inCart ? ' more available (' + v.inCart + ' already in your cart)' : ' available'); }
    // msg replaces the stock line; after an add, prefix comes before it ("Only 2 more were available, so we added 2.").
    function limit(v, msg, prefix) {
      v = v || current();
      if (!v) return;
      var l = left(v), n = v.inCart || 0, full = v.available && l === 0;
      if (qty) { qty.max = Math.max(1, Math.min(maxQty, l)); if ((parseInt(qty.value, 10) || 1) > +qty.max) qty.value = qty.max; }
      q('[data-j5-add]').forEach(function (el) { el.textContent = !v.available ? 'Sold out' : full ? 'All in your cart' : 'Add to cart · ' + money(v.price); });
      if (add) { add.disabled = !v.available || full; add.removeAttribute('aria-disabled'); }
      // With every pack already in the cart, "Buy now" goes straight to checkout.
      if (now) now.disabled = !v.available;
      var cartLink = ' <a href="' + cartRoute('cart_url', '/cart') + '">Go to cart</a>';
      var byMax = v.stock == null || v.stock > maxQty;
      var line = full ? (byMax ? 'You have the most we sell per order (' + maxQty + ') in your cart.' : 'All ' + v.stock + ' we have of this pack ' + (v.stock === 1 ? 'is' : 'are') + ' in your cart.') + cartLink
        : n > 0 ? n + ' already in your cart' + (l !== Infinity ? ' · ' + l + ' more available' : '') + '.' + cartLink : '';
      note(msg || (prefix ? prefix + ' ' : '') + line);
    }
    // Keep the requested quantity within what is left; returns the quantity to send (0 = nothing left).
    function clampQty() {
      var v = current(), l = left(v), want = Math.max(1, parseInt(qty.value, 10) || 1);
      if (want <= l) return want;
      if (l > 0) {
        qty.value = l;
        limit(v, onlyLeft(v, l) + ', so we’ve set the quantity to ' + l + '.');
        pendingNote = 'Only ' + l + (v.inCart ? ' more' : '') + (l === 1 ? ' was' : ' were') + ' available, so we added ' + l + '.';
      }
      return l;
    }
    cartListeners.push(function (counts) {
      variants.forEach(function (v) { v.inCart = counts[v.id] || 0; });
      limit(current(), null, pendingNote);
      pendingNote = '';
    });
    var pendingNote = '';
    // Runs before Dawn's product-form handler (capture phase), so the quantity it posts is already within stock.
    var form = root.querySelector('product-form form');
    document.addEventListener('submit', function (e) {
      if (e.target !== form) return;
      if (clampQty() === 0) { e.preventDefault(); e.stopPropagation(); limit(current()); }
    }, true);
    // Stock sold elsewhere since the page loaded: Shopify refuses the add. Re-read the cart and treat the pack as full.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartError, function (ev) {
          var v = variants.find(function (x) { return String(x.id) === String(ev && ev.productVariantId); });
          if (!v) return;
          fetchCart().then(function (cart) {
            syncCart(cart);
            v.stock = v.inCart || 0;
            if (!v.stock) { v.available = false; show(v); }
            var err = root.querySelector('.j5-pdp__error'); if (err) err.hidden = true;
            limit(v, v.stock ? 'Sorry, we only have ' + v.stock + ' of this pack right now and ' + (v.stock === 1 ? 'it is' : 'they are all') + ' in your cart. <a href="' + cartRoute('cart_url', '/cart') + '">Go to cart</a>' : 'Sorry, this pack has just sold out.');
          }).catch(function () {});
        });
      }
    } catch (err) { /* Dawn's pub/sub not on this page */ }

    var packs = Array.prototype.slice.call(q('.j5-pack'));
    packs.forEach(function (b, i) {
      b.tabIndex = b.classList.contains('is-active') ? 0 : -1;
      b.addEventListener('click', function () { show(variants.find(function (v) { return v.id === +b.dataset.variant; })); });
      b.addEventListener('keydown', function (e) {
        var d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var next = packs[(i + d + packs.length) % packs.length];
        next.focus(); next.click();
      });
    });

    q('.j5-qty__btn').forEach(function (b) {
      b.addEventListener('click', function () {
        var want = (parseInt(qty.value, 10) || 1) + +b.dataset.step, l = left(current());
        pendingNote = '';
        if (want > l && l !== Infinity && l > 0) limit(current(), onlyLeft(current(), l) + '.');
        qty.value = Math.max(1, Math.min(maxQty, l, want));
      });
    });

    if (qty) qty.addEventListener('change', function () { clampQty(); });

    var checkoutUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'checkout';
    if (now) now.addEventListener('click', function () {
      var n = clampQty();
      if (n === 0) { window.location.href = checkoutUrl; return; }
      now.disabled = true;
      var body = { items: [{ id: +idInput.value, quantity: n }] };
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      }).then(function (r) { if (!r.ok) throw r; window.location.href = checkoutUrl; })
        .catch(function () {
          now.disabled = false;
          // Usually stock: re-read the cart; if the pack is in it, checkout still has something to buy.
          fetchCart().then(function (cart) { syncCart(cart); var v = current(); if (v.inCart) { window.location.href = checkoutUrl; return; } var w = root.querySelector('.j5-pdp__error'); if (w) { w.hidden = false; w.querySelector('.product-form__error-message').textContent = 'Could not add this pack. Please try again.'; } }).catch(function () {});
        });
    });

    limit(current());

    q('.j5-pdp__thumb').forEach(function (t) {
      t.addEventListener('click', function () {
        if (t.dataset.video) { if (stageBox) playVideo(t); }
        else { closeVideo(); if (stage) { stage.src = t.dataset.src; stage.removeAttribute('srcset'); stage.alt = t.dataset.alt || ''; } }
        q('.j5-pdp__thumb').forEach(function (x) { x.classList.toggle('is-active', x === t); x.setAttribute('aria-pressed', x === t ? 'true' : 'false'); });
      });
    });
  }

  // ---------- 3. Policy title accent ----------
  function accentPolicyTitle() {
    var h = document.querySelector('.shopify-policy__title h1');
    if (!h || h.querySelector('em')) return;
    var words = h.textContent.trim().split(/\s+/);
    if (words.length < 2) return;
    var last = words.pop();
    h.textContent = words.join(' ') + ' ';
    var em = document.createElement('em');
    em.textContent = last;
    h.appendChild(em);
  }

  // Filter bar: choosing a sort option reloads with it (the form keeps the active filters).
  function initSort(root) {
    (root || document).querySelectorAll('form[data-j5-sort]').forEach(function (f) {
      if (f.dataset.j5Ready) return; f.dataset.j5Ready = '1';
      var sel = f.querySelector('select');
      if (sel) sel.addEventListener('change', function () { f.submit(); });
    });
  }

  // ---------- 4. Header search typeahead (Shopify predictive search) ----------
  var BOTTLE = '<svg class="j5-sugg__ph" viewBox="0 0 60 100" width="18" height="30" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true" focusable="false"><path d="M22 4h16v10l6 8v70a4 4 0 0 1-4 4H20a4 4 0 0 1-4-4V22l6-8z"/><path d="M22 14h16"/></svg>';
  function esc(t) { return String(t == null ? '' : t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function rupees(v) { var n = Number(v); if (!isFinite(n)) return ''; return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }); }
  function suggest(form, q) {
    if (window.J5_SUGGEST_MOCK) return Promise.resolve(window.J5_SUGGEST_MOCK(q));
    var url = (form.dataset.suggestUrl || '/search/suggest') + '.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last';
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) { return r.ok ? r.json() : { resources: { results: {} } }; });
  }
  function initTypeahead(form) {
    if (form.dataset.j5Ready) return;
    form.dataset.j5Ready = '1';
    var input = form.querySelector('input[type="search"]');
    var list = form.querySelector('.j5-sugg');
    if (!input || !list) return;
    var timer = 0, active = -1, seq = 0;
    function items() { return list.querySelectorAll('[role="option"]'); }
    function open() { list.hidden = false; input.setAttribute('aria-expanded', 'true'); form.classList.add('is-suggesting'); }
    function close() { list.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); form.classList.remove('is-suggesting'); active = -1; }
    function setActive(i) {
      var all = items(); if (!all.length) return;
      if (active > -1 && all[active]) all[active].classList.remove('is-active');
      active = (i + all.length) % all.length;
      all[active].classList.add('is-active');
      input.setAttribute('aria-activedescendant', all[active].id);
      all[active].scrollIntoView({ block: 'nearest' });
    }
    function render(q, data) {
      var res = (data && data.resources && data.resources.results) || {};
      var prods = res.products || [], cols = res.collections || [];
      var id = input.id, html = '', n = 0;
      if (prods.length) {
        html += '<p class="j5-sugg__label">Products</p>';
        prods.forEach(function (p) {
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          var price = p.price != null ? rupees(p.price) : '';
          var mrp = Number(p.compare_at_price_max) > Number(p.price) ? rupees(p.compare_at_price_max) : '';
          html += '<a class="j5-sugg__item" role="option" id="' + id + '-o' + (n++) + '" href="' + esc(p.url) + '">' +
            '<span class="j5-sugg__img">' + (img ? '<img src="' + esc(img) + (img.indexOf('?') > -1 ? '&' : '?') + 'width=96" alt="" width="48" height="48" loading="lazy">' : BOTTLE) + '</span>' +
            '<span class="j5-sugg__text"><span class="j5-sugg__title">' + esc(p.title) + '</span>' +
            '<span class="j5-sugg__price">' + price + (mrp ? ' <s>' + mrp + '</s>' : '') + '</span></span></a>';
        });
      }
      if (cols.length) {
        html += '<p class="j5-sugg__label">Categories</p><div class="j5-sugg__chips">';
        cols.forEach(function (c) { html += '<a class="j5-sugg__chip" role="option" id="' + id + '-o' + (n++) + '" href="' + esc(c.url) + '">' + esc(c.title) + '</a>'; });
        html += '</div>';
      }
      if (!prods.length && !cols.length) html += '<p class="j5-sugg__empty">No products match “' + esc(q) + '”. Try a grade (5W-30), a MANNOL code (7901) or a specification (DOT-4).</p>';
      html += '<a class="j5-sugg__all" role="option" id="' + id + '-o' + (n++) + '" href="' + esc(form.getAttribute('action')) + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last&type=product">See all results for “' + esc(q) + '” <span aria-hidden="true">→</span></a>';
      list.innerHTML = html;
      active = -1;
      open();
    }
    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { close(); return; }
      timer = setTimeout(function () {
        var mine = ++seq;
        suggest(form, q).then(function (d) { if (mine === seq && input.value.trim() === q) render(q, d); }).catch(function () { close(); });
      }, 160);
    });
    input.addEventListener('keydown', function (e) {
      if (list.hidden) { if (e.key === 'ArrowDown' && input.value.trim().length > 1) { input.dispatchEvent(new Event('input')); } return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Enter' && active > -1) { e.preventDefault(); items()[active].click(); }
    });
    input.addEventListener('focus', function () { if (list.innerHTML && input.value.trim().length > 1) open(); });
    document.addEventListener('click', function (e) { if (!form.contains(e.target)) close(); });
  }
  // Phone: the search icon opens the header search bar (with suggestions) instead of leaving the page.
  function initSearchToggle(root) {
    (root || document).querySelectorAll('[data-j5-search-toggle]').forEach(function (a) {
      if (a.dataset.j5Ready) return; a.dataset.j5Ready = '1';
      a.addEventListener('click', function (e) {
        var hdr = a.closest('.j5-hdr'); var input = hdr && hdr.querySelector('.j5-hdr__search input');
        if (!input) return;
        e.preventDefault();
        var openNow = !hdr.classList.contains('is-search-open');
        hdr.classList.toggle('is-search-open', openNow);
        a.setAttribute('aria-expanded', openNow ? 'true' : 'false');
        if (openNow) input.focus();
      });
    });
  }
  // Cart pop-up: sit just under the header, or 16px from the top once the header has scrolled away (desktop).
  function placeCartPopup() {
    var hdr = document.querySelector('.j5-hdr');
    var top = hdr ? Math.max(16, hdr.getBoundingClientRect().bottom + 12) : 16;
    document.documentElement.style.setProperty('--j5-cn-top', top + 'px');
  }
  window.addEventListener('scroll', placeCartPopup, { passive: true });
  window.addEventListener('resize', placeCartPopup);

  // ---------- 5. In-cart stepper on product cards ----------
  function cartRoute(key, fallback) { return (window.routes && window.routes[key]) || fallback; }
  var cartListeners = []; // product pages: told the quantity of every variant in the cart after each sync
  // "+" stops at the pack's stock (data-stock, empty when not limited) or the per-item maximum (data-max);
  // the card's stock line says why.
  function limitStep(s) {
    var n = parseInt(s.getAttribute('data-qty'), 10) || 0, stock = parseInt(s.getAttribute('data-stock'), 10), most = parseInt(s.getAttribute('data-max'), 10) || 100;
    var byMax = isNaN(stock) || stock > most, max = byMax ? most : stock;
    var full = n >= max;
    var plus = s.querySelector('[data-j5-step-by="1"]');
    if (plus) { plus.disabled = full; plus.setAttribute('aria-label', full ? (byMax ? 'Maximum per order reached' : 'No more in stock') : 'One more'); plus.title = full ? (byMax ? 'You can order up to ' + most + ' of each pack' : 'That’s all the stock we have') : ''; }
    var line = s.closest('.j5-card') && s.closest('.j5-card').querySelector('.j5-card__stock');
    if (line) line.textContent = full ? (byMax ? 'Max ' + most + ' per order' : 'All ' + max + ' in your cart') : 'In stock';
  }
  function syncCart(cart) {
    if (!cart || !cart.items) return;
    var qty = {};
    cart.items.forEach(function (i) { qty[i.variant_id] = (qty[i.variant_id] || 0) + i.quantity; });
    document.querySelectorAll('[data-j5-step]').forEach(function (s) {
      var n = qty[s.getAttribute('data-variant')] || 0;
      s.hidden = n === 0;
      var o = s.querySelector('[data-j5-step-qty]'); if (o) o.textContent = n;
      s.setAttribute('data-qty', n);
      limitStep(s);
    });
    document.querySelectorAll('[data-j5-add]').forEach(function (f) { f.hidden = (qty[f.getAttribute('data-variant')] || 0) > 0; });
    cartListeners.forEach(function (fn) { fn(qty); });
  }
  function fetchCart() {
    return fetch(cartRoute('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  function updateBubble(sections) {
    var html = sections && sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (!html || !bubble) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var inner = doc.querySelector('.shopify-section');
    bubble.innerHTML = inner ? inner.innerHTML : html;
  }
  function changeQty(step, n) {
    var id = step.getAttribute('data-variant');
    step.classList.add('is-busy');
    step.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
    return fetch(cartRoute('cart_change_url', '/cart/change') + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(id), quantity: Math.max(0, n), sections: 'cart-icon-bubble', sections_url: location.pathname }),
    }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); })
      .then(function (cart) { updateBubble(cart.sections); return { cart: cart, stockCapped: true }; })
      // Refused: show the cart as it is. A 422 means the stock ran out (sold elsewhere since the page loaded).
      .catch(function (err) { return fetchCart().then(function (cart) { return { cart: cart, stockCapped: !!(err && err.status === 422) }; }); })
      .then(function (res) {
        // Fewer in the cart than asked (Shopify caps at its stock, or refused): that is the pack's new limit.
        var got = 0;
        (res.cart && res.cart.items || []).forEach(function (i) { if (String(i.variant_id) === String(id)) got += i.quantity; });
        if (res.stockCapped && n > got) step.setAttribute('data-stock', got);
        var cart = res.cart;
        step.classList.remove('is-busy'); step.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
        syncCart(cart);
      })
      .catch(function () { step.classList.remove('is-busy'); step.querySelectorAll('button').forEach(function (b) { b.disabled = false; }); limitStep(step); });
  }
  document.addEventListener('click', function (e) {
    var step = e.target.closest && e.target.closest('[data-j5-step]');
    if (!step) return;
    var current = parseInt((step.querySelector('[data-j5-step-qty]') || {}).textContent, 10) || 0;
    if (e.target.closest('[data-j5-step-del]')) { e.preventDefault(); changeQty(step, 0); }
    else if (e.target.closest('[data-j5-step-by]')) { e.preventDefault(); changeQty(step, current + parseInt(e.target.closest('[data-j5-step-by]').getAttribute('data-j5-step-by'), 10)); }
  });
  // After "Add to cart" (Dawn publishes cartUpdate) and on load (pages can be cached), sync every card with the cart.
  // Dawn declares PUB_SUB_EVENTS with const (a global binding, not a window property) and subscribe() as a function.
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { fetchCart().then(syncCart).catch(function () {}); });
    }
  } catch (e) { /* Dawn's pub/sub not on this page */ }
  if (!window.J5_SUGGEST_MOCK && document.querySelector('[data-j5-step], .j5-pdp')) fetchCart().then(syncCart).catch(function () {});

  // ---------- 6. Oil finder without Search & Discovery ----------
  // The home-page finder links to ?filter.p.m.specs.vehicle=…&filter.p.m.specs.viscosity=…. Shopify applies those
  // only once the Vehicle type / Viscosity grade filters are added in Search & Discovery; until then it ignores
  // them and shows every product. The filter bar lists the filters Shopify does apply (data-j5-known); for the
  // finder's filters missing from that list, load the whole collection (templates/collection.j5-finder.liquid)
  // and keep the matching cards. Once the app filters exist this does nothing.
  var FINDER = [
    { param: 'filter.p.m.specs.vehicle', attr: 'data-vehicle', label: 'Vehicle type', list: true },
    { param: 'filter.p.m.specs.viscosity', attr: 'data-grade', label: 'Viscosity grade', list: false },
  ];
  function finderLoad(url, acc) {
    return fetch(url, { headers: { Accept: 'text/html' } }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); }).then(function (html) {
      var list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-j5-finder-all]');
      if (!list) throw new Error('collection.j5-finder template missing');
      acc = acc.concat(Array.prototype.slice.call(list.children));
      var next = list.getAttribute('data-next');
      if (!next) return acc;
      var u = new URL(next, location.href); u.searchParams.set('view', 'j5-finder');
      return finderLoad(u.toString(), acc);
    });
  }
  function initFinderFallback() {
    var bar = document.getElementById('J5FilterBar');
    if (!bar || bar.dataset.j5Finder || bar.getAttribute('data-j5-results') !== 'collection') return;
    var known = (bar.getAttribute('data-j5-known') || '').split(',');
    var here = new URL(location.href);
    var want = [];
    FINDER.forEach(function (f) {
      if (known.indexOf(f.param) !== -1) return;
      var values = here.searchParams.getAll(f.param).map(function (v) { return v.trim(); }).filter(Boolean);
      if (values.length) want.push({ f: f, values: values });
    });
    if (!want.length) return;
    bar.dataset.j5Finder = '1';

    // Links and the sort form built by Shopify drop the filters it does not know: put ours back.
    function withFinder(href, skip) {
      var u = new URL(href, location.href);
      want.forEach(function (w) { w.values.forEach(function (v) { if ((skip && skip.param === w.f.param && skip.value === v) || u.searchParams.getAll(w.f.param).indexOf(v) !== -1) return; u.searchParams.append(w.f.param, v); }); });
      u.searchParams.delete('page');
      return u.toString();
    }
    bar.querySelectorAll('.j5-fchip[href]').forEach(function (a) { a.href = withFinder(a.getAttribute('href')); });
    var sort = bar.querySelector('[data-j5-sort]');
    if (sort) want.forEach(function (w) { w.values.forEach(function (v) { var i = document.createElement('input'); i.type = 'hidden'; i.name = w.f.param; i.value = v; sort.appendChild(i); }); });
    var chips = bar.querySelector('.j5-fbar__chips');
    var base = new URL(location.href);
    want.forEach(function (w) { base.searchParams.delete(w.f.param); });
    want.forEach(function (w) {
      w.values.forEach(function (v) {
        var a = document.createElement('a');
        a.className = 'j5-fchip is-active is-removable';
        a.href = withFinder(base.toString(), { param: w.f.param, value: v });
        a.setAttribute('aria-label', 'Remove filter ' + w.f.label + ': ' + v);
        a.innerHTML = esc(v) + ' <span aria-hidden="true">×</span>';
        if (chips) chips.appendChild(a);
      });
    });

    var grid = document.getElementById('product-grid');
    var box = document.getElementById('ProductGridContainer') || (grid && grid.parentNode);
    if (!grid || !box) return;
    box.classList.add('j5-finding');
    box.setAttribute('aria-busy', 'true');
    var src = new URL(base.toString()); src.searchParams.delete('page'); src.searchParams.set('view', 'j5-finder');
    finderLoad(src.toString(), []).then(function (items) {
      // Same filter twice (e.g. two grades) = either value; different filters = both, as Shopify does.
      var hits = items.filter(function (li) {
        return want.every(function (w) {
          var raw = (li.getAttribute(w.f.attr) || '').toLowerCase();
          var have = w.f.list ? raw.split('|') : [raw];
          return w.values.some(function (v) { return have.indexOf(v.toLowerCase()) !== -1; });
        });
      });
      box.querySelectorAll('.pagination-wrapper').forEach(function (p) { p.remove(); });
      var count = bar.querySelector('.j5-fbar__count');
      if (count) count.innerHTML = '<strong>' + hits.length + '</strong> ' + (hits.length === 1 ? 'product' : 'products');
      if (!hits.length) {
        var picked = want.map(function (w) { return w.values.join(' or '); }).join(' · ');
        var empty = document.createElement('div');
        empty.className = 'j5-fempty';
        empty.innerHTML = '<p>No products match <strong>' + esc(picked) + '</strong> yet.</p><p><a class="j5-btn j5-btn--outline j5-btn--sm" href="' + esc(base.toString()) + '">Show all products</a> <a class="j5-btn j5-btn--sm" href="/pages/contact">Ask us which oil fits</a></p>';
        grid.replaceWith(empty);
      } else {
        grid.innerHTML = '';
        hits.forEach(function (li) { grid.appendChild(document.adoptNode(li)); });
        fetchCart().then(syncCart).catch(function () {});
      }
    }).catch(function (e) { if (window.console) console.warn('J5 finder fallback:', e); })
      .then(function () { box.classList.remove('j5-finding'); box.removeAttribute('aria-busy'); });
  }

  // ---------- 7. Announcement bar: only while its discount code is live ----------
  // Liquid cannot see discount codes, so the bar is rendered hidden and the Storefront API (tokenless cart access,
  // same origin) is asked about the code on a throwaway cart holding one in-stock pack. The bar is hidden only when
  // Shopify answers that the code does not apply (deleted, disabled or expired in admin); with no clear answer
  // (password-protected store, network error) it stays visible.
  // The answer is kept for 15 minutes per browser tab session.
  function initPromo() {
    var bar = document.querySelector('[data-j5-promo]');
    if (!bar || bar.dataset.j5Ready) return;
    bar.dataset.j5Ready = '1';
    var code = bar.getAttribute('data-code'), variant = bar.getAttribute('data-variant');
    if (!code || !variant) return;
    var key = 'j5-promo-v2:' + code.toUpperCase(), t = Date.now();
    try { var c = JSON.parse(sessionStorage.getItem(key) || 'null'); if (c && t - c.t < 9e5) { bar.hidden = !c.ok; return; } } catch (e) {}
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'api/' + (bar.getAttribute('data-api') || '2026-10') + '/graphql.json', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: 'mutation J5Promo($input: CartInput!) { cartCreate(input: $input) { cart { discountCodes { code applicable } } } }',
        variables: { input: { lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variant, quantity: 1 }], discountCodes: [code] } },
      }),
    }).then(function (r) { return r.json(); }).then(function (j) {
      var cart = j && j.data && j.data.cartCreate && j.data.cartCreate.cart;
      if (!cart) { bar.hidden = false; return; } // no verdict: keep the bar, don't cache
      var ok = (cart.discountCodes || []).some(function (d) { return String(d.code).toUpperCase() === code.toUpperCase() && d.applicable; });
      bar.hidden = !ok;
      try { sessionStorage.setItem(key, JSON.stringify({ ok: ok, t: t })); } catch (e) {}
    }).catch(function () { bar.hidden = false; });
  }

  // ---------- 8. Cart page GST details ----------
  // Optional GSTIN + business name (snippets/j5-gst-fields.liquid). The inputs belong to the cart form, so "Check out"
  // saves them as order attributes; they are also saved as the shopper types. An invalid GSTIN blocks checkout with a
  // message, and a GSTIN needs a business name.
  var GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
  function initGst(root) {
    (root || document).querySelectorAll('[data-j5-gst]').forEach(function (box) {
      if (box.dataset.j5Ready) return;
      box.dataset.j5Ready = '1';
      var gst = box.querySelector('[data-j5-gstin]'), name = box.querySelector('[data-j5-gst-name]'), err = box.querySelector('[data-j5-gst-error]');
      if (!gst) return;
      var timer;
      function check() {
        var v = gst.value.replace(/\s+/g, '').toUpperCase(), bad = !!v && !GSTIN.test(v);
        gst.setCustomValidity(bad ? 'Enter a valid 15-character GSTIN, for example 27ABCDE1234F1Z5.' : '');
        if (name) name.required = !!v;
        if (err) err.hidden = !bad;
        return !bad;
      }
      function save() {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (!check()) return;
          fetch(cartRoute('cart_update_url', '/cart/update') + '.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { GSTIN: gst.value.replace(/\s+/g, '').toUpperCase(), 'GST business name': name ? name.value.trim() : '' } }),
          }).catch(function () {});
        }, 500);
      }
      gst.addEventListener('input', save);
      gst.addEventListener('blur', function () { gst.value = gst.value.replace(/\s+/g, '').toUpperCase(); check(); });
      if (name) name.addEventListener('input', save);
      // The browser stops checkout on an invalid field; open the panel so it can show the message.
      [gst, name].forEach(function (el) { if (el) el.addEventListener('invalid', function () { box.open = true; }); });
      check();
    });
  }

  function init(root) {
    enhanceAll(root);
    initPromo();
    initGst(root);
    (root || document).querySelectorAll('form[data-j5-typeahead]').forEach(initTypeahead);
    initSearchToggle(root);
    placeCartPopup();
    initSort(root);
    (root || document).querySelectorAll('.j5-pdp').forEach(initProduct);
    accentPolicyTitle();
    initFinderFallback();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // Sections re-rendered by Dawn (filters, sort, theme editor) bring new selects.
  new MutationObserver(function (records) {
    records.forEach(function (r) { r.addedNodes.forEach(function (n) { if (n.nodeType === 1) { if (n.tagName === 'SELECT') enhance(n); else if (n.querySelector) init(n); } }); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
